import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.css";
import Button from "react-bootstrap/Button";
import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";
import Container from "react-bootstrap/Container";
import { toast, ToastContainer } from 'react-toastify';
import { auth } from "../base";
import { signInWithEmailAndPassword } from "firebase/auth"; // Import Firebase auth functions
import { useTranslation } from "react-i18next";
import AdminModal from "../components/admin/AdminModal"
import AdminNewsModal from "../components/admin/AdminNewsModal"
import { Link } from 'react-router-dom';
import { AccountCircle } from '@mui/icons-material';
import AccountModal from '../components/userAccount/AccountModal';
import WhatsNewButton from '../components/whatsNew/WhatsNewButton';
import { useProjectLimits } from '../hooks/useProjectLimits';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../base';
import featureFlags from '../config/featureFlags';

function SiteNav(props) {
  const { t } = useTranslation(["translation"]);
  const currentUser = props.currentUser;
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  const [userData, setUserData] = useState(null);

  // Get project limits data
  const { projectCount, projectLimit, isLoading, canCreateProject, isAtLimit } = useProjectLimits(currentUser, userData);

  // Real-time listener for user data to track plan changes
  useEffect(() => {
    if (!currentUser || currentUser.email.startsWith("guest_")) {
      setUserData(null);
      return;
    }

    const userDocRef = doc(db, 'users', currentUser.uid);
    const unsubscribe = onSnapshot(userDocRef, (doc) => {
      if (doc.exists()) {
        setUserData(doc.data());
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 600);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  

  const showToastMessage = () => {
    const message = t('welcome-back-message', {
      defaultValue: 'Welcome back! Why not register today? It’s free!',
    });
    const buttonText = t('register-now', {
      defaultValue: 'Register Now',
    });

    toast.info(
      <div>
        <p>{message}</p>
        <Button
          variant="primary"
          onClick={() => {
            toast.dismiss(); // Close the toast
            handleGuestLogoutAndSignup(); // Perform your signup logic
          }}
        >
          {buttonText}
        </Button>
      </div>,
      {
        position: "top-center", // Display at the top center
        autoClose: false, // Prevent auto-close
        closeOnClick: false, // Only dismiss with the close button
        closeButton: true, // Show the close button
        hideProgressBar: true, // Hide the progress bar
        draggable: false, // Disable dragging to dismiss
        pauseOnHover: true, // Pause the toast on hover
      }
    );
  };

  const handleGuestLogoutAndSignup = async () => {
    try {
      // Log out the guest
      await auth.signOut();

      // Open the signup modal
      props.closeSignup(true);
    } catch (error) {
      console.error("Error logging out guest:", error);
    }
  };


  const handleStartProject = () => {
    const proceed = () => {
      if (props.closeCurrentProjectForNewProjectFlow) {
        props.closeCurrentProjectForNewProjectFlow();
      } else if (props.captureCurrentProjectStateForNewProjectFlow) {
        props.captureCurrentProjectStateForNewProjectFlow();
      }
      props.closeUpload(true);
    };

    // Only check project limits if feature flag is enabled
    if (featureFlags.PROJECT_LIMITS_ENABLED && !canCreateProject && !isLoading) {
      // Show error message when at limit
      const planName = userData?.planName?.replace('_plan', '').replace('_', ' ') || 'current plan';
      toast.error(
        t('projectLimitReached', {
          current: projectCount,
          limit: projectLimit,
          plan: planName,
          defaultValue: `Project limit reached! You have ${projectCount}/${projectLimit} projects with your ${planName}. Please upgrade your plan to create more projects.`
        }),
        { position: 'top-center', autoClose: 5000 }
      );
      return;
    }

    if (props.requestEditorExitConfirmation) {
      props.requestEditorExitConfirmation({ onProceed: proceed });
      return;
    }

    proceed();
  };

  const handleDesignerClick = () => {
    if (currentUser) {
      props.setDesignerModalOpen(true);
    } else {
      props.showConnectionRequiredToast(t);
    }
  };

  return (
    <>

      <Navbar
        className="siteBackgroundColor siteNavBar"
        collapseOnSelect
        expand="lg"
        variant="dark"
      >
        <Container fluid>
          <div className="header-container">


            <div className="brand-and-slogan-container">
              <Navbar.Brand className="siteName">
                <Link to="/">
                  {t("app-name")}
                </Link>
              </Navbar.Brand>
              <div className="slogan">{t("slogan")}</div>
              {/* Hidden admin components that can be triggered from AdminDashboardModal */}
              {props.userRole === 'mainAdmin' && (
                <div style={{ display: 'none' }}>
                  <AdminModal />
                  <AdminNewsModal />
                </div>
              )}
            </div>


            {/* Designer link removed from header so it can be positioned over the canvas */}
            <Navbar.Toggle aria-controls="responsive-navbar-nav" />
          </div>

          <Navbar.Collapse id="responsive-navbar-nav">
            <Nav className="me-auto">
              {/* Empty - we'll use navEnd for all items in proper order */}
            </Nav>

            <Nav className="navEnd">
              {!currentUser ? (
                <>
                  {/* Not connected: What's New → Guest → Signup → Login */}
                  <div className="d-flex align-items-center" style={{ flexShrink: 0 }}>
                    <WhatsNewButton handleOpenContactForm={props.handleOpenContactForm} />
                  </div>
                  <Nav.Link
                    title="Try as Guest"
                    id="guest-login"
                    className="userDropdown"
                    eventKey={6}
                    onClick={props.handleGuestLogin}
                  >
                    {t("try-guest")}
                  </Nav.Link>
                  <Nav.Link
                    title="Start here"
                    id="user-dropdown"
                    className="userDropdown"
                    eventKey={4}
                    onClick={() => {
                      props.setLogin(false);
                      props.setIsLoginOut(false);
                      props.closeSignup(true);
                    }}
                  >
                    {t("signup")}
                  </Nav.Link>
                  <Nav.Link
                    title="Start here"
                    id="user-dropdown"
                    className="userDropdown"
                    eventKey={5}
                    onClick={() => {
                      props.setLogin(true);
                      props.setIsLoginOut(false);
                      props.closeSignup(true);
                    }}
                  >
                    {t("login")}
                  </Nav.Link>
                </>
              ) : currentUser.email.startsWith("guest_") ? (
                <>
                  {/* Guest user: What's New → Signup */}
                  <div className="d-flex align-items-center" style={{ flexShrink: 0 }}>
                    <WhatsNewButton handleOpenContactForm={props.handleOpenContactForm} />
                  </div>
                  <Nav.Link
                    title="Signup"
                    id="guest-signup"
                    className="userDropdown"
                    eventKey={4}
                    onClick={() => {
                      handleGuestLogoutAndSignup()
                        .then(() => {
                          props.setLogin(false);
                          props.setIsLoginOut(false);
                          props.closeSignup(true);
                        })
                        .catch((error) => {
                          console.error("Error during guest sign out:", error);
                        });
                    }}
                  >
                    {t("signup")}
                  </Nav.Link>
                </>
              ) : (
                <>
                  {/* Logged in user: What's New → New Project → Open Project → User Icon */}
                  <div className="d-flex align-items-center" style={{ flexShrink: 0 }}>
                    <WhatsNewButton handleOpenContactForm={props.handleOpenContactForm} />
                  </div>
                  {(props.userRole === 'mainAdmin' || props.userRole === 'admin') && (
                    <Nav.Link
                      title="Test Guest Mode"
                      id="admin-guest-login"
                      className="userDropdown"
                      eventKey="guest-test"
                      onClick={() => props.handleGuestLogin({ allowAdminTest: true })}
                    >
                      {t('testGuestMode', { defaultValue: 'Test Guest' })}
                    </Nav.Link>
                  )}
                  <Nav.Link
                    eventKey="upload"
                    onClick={handleStartProject}
                    className={featureFlags.PROJECT_LIMITS_ENABLED && !canCreateProject && !isLoading ? 'text-muted' : ''}
                    style={{ 
                      cursor: featureFlags.PROJECT_LIMITS_ENABLED && !canCreateProject && !isLoading ? 'not-allowed' : 'pointer',
                      opacity: featureFlags.PROJECT_LIMITS_ENABLED && !canCreateProject && !isLoading ? 0.6 : 1
                    }}
                    title={featureFlags.PROJECT_LIMITS_ENABLED && !canCreateProject && !isLoading ? 
                      `Project limit reached (${projectCount}/${projectLimit})` : 
                      t("start-project")
                    }
                  >
                    {t("start-project")} {featureFlags.PROJECT_LIMITS_ENABLED && !isLoading && !canCreateProject && `(${projectCount}/${projectLimit})`}
                  </Nav.Link>
                  <Nav.Link
                    eventKey="4"
                    onClick={() => {
                      const proceed = () => {
                        props.closeAccount(true);
                        props.setIsProjectList(true);
                      };

                      if (props.requestEditorExitConfirmation) {
                        props.requestEditorExitConfirmation({ onProceed: proceed });
                        return;
                      }

                      proceed();
                    }}
                  >
                    {t("view-projects")}
                  </Nav.Link>
                  <div className="profile-icon-container">
                    <Button variant="outline-secondary" onClick={() => props.setIsAccountModalOpen(true)}>
                      <AccountCircle className="profile-icon" />
                    </Button>
                  </div>
                </>
              )}
            </Nav>

          </Navbar.Collapse>
        </Container>



        {/* Modal Component */}
        <AccountModal
          isOpen={props.isAccountModalOpen}
          onRequestClose={() => props.setIsAccountModalOpen(false)}
          handleLogOut={props.handleLogOut}
          currentUser={currentUser}
          userRole={props.userRole}
        />
      </Navbar>
    </>
  );
}

export default SiteNav;
